import type { ImportRunRowOutcome, ImportUnmatchedField } from "src/graphql/generated";

/**
 * Whether a run's source link may be rendered as a link.
 *
 * `sourceUrl` is stored exactly as somebody pasted it, and the run that stored it may
 * be one that failed *because* the link was wrong — so this column holds arbitrary
 * operator text, not a validated address. Putting that straight into an `href` is how
 * `javascript:` reaches a click in a console whose whole audience is iam-admins.
 *
 * So a link is rendered only for an absolute http(s) address, and everything else —
 * a bare spreadsheet id, which the API also accepts, and anything malformed — is
 * shown as text. Narrowing further to docs.google.com was considered and rejected:
 * the host the reader accepts lives in `internal/sheets/link.go`, and a second copy
 * of it here would be a rule with nothing to keep the two in step. What this refuses
 * is a scheme the browser would execute, which is a fact about browsers rather than
 * about that file.
 */
export function sheetHref(sourceUrl: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(sourceUrl);
  } catch {
    return null;
  }
  return parsed.protocol === "https:" || parsed.protocol === "http:" ? sourceUrl : null;
}

/**
 * The three lookups a value can fail to resolve against, named as the console names
 * them elsewhere.
 *
 * A `Record` over the union rather than a switch with a default, so a fourth
 * `ImportUnmatchedField` fails to compile here instead of rendering as one of the
 * other three — which would send an operator to correct a firm's name when the value
 * that would not resolve was an entity.
 */
const unmatchedFieldLabels: Record<ImportUnmatchedField, string> = {
  DEPARTMENT: "Department",
  ENTITY: "Entity",
  NPD_SUBCATEGORY: "Subcategory",
  EXTERNAL_PARTNER_ORG: "Partner organization"
};

export function humanUnmatchedField(field: ImportUnmatchedField): string {
  return unmatchedFieldLabels[field];
}

/**
 * Which outcomes put something in the database.
 *
 * A `Record` for the same reason as above, and here it is load bearing: a fifth
 * outcome has to be classified here or this file stops compiling, which is what makes
 * the list below a fact about the enum rather than a list somebody kept in step.
 */
const outcomeImported: Record<ImportRunRowOutcome, boolean> = {
  CREATED: true,
  ATTACHED: true,
  SKIPPED: false,
  FAILED: false
};

/**
 * The outcomes that did not import, which is what the report asks the server for.
 *
 * Derived from the record above rather than written out again. The report is a page of
 * a run that can be twenty thousand rows long, and a page of the whole report can carry
 * no failures at all — so the narrowing happens server-side, and this is the list it
 * narrows to. A second hand-written copy would be a second place a fifth outcome has to
 * be added, failing silently in the one direction that matters: a row the server never
 * sent and the screen would have shown.
 */
export const UNIMPORTED_OUTCOMES: ImportRunRowOutcome[] = (
  Object.keys(outcomeImported) as ImportRunRowOutcome[]
).filter((outcome) => !outcomeImported[outcome]);

const outcomeLabels: Record<ImportRunRowOutcome, string> = {
  CREATED: "Created",
  ATTACHED: "Attached",
  SKIPPED: "Skipped",
  FAILED: "Failed"
};

export function humanOutcome(outcome: ImportRunRowOutcome): string {
  return outcomeLabels[outcome];
}
